import { useEffect, useMemo, useState } from "react";

import {
  createAIRun,
  loadAIRun,
  loadProviderStatus,
  loadUseCases,
  reviewAIRun,
  retryAIRun,
  type AIReviewDecision,
  type AIProviderStatus,
  type AIUseCase,
  type AIRunView,
} from "./api";
import {
  aiModeLabel,
  statusLabel,
  useCaseLabel,
} from "../../localization/zh-CN";
import "./ai-workbench.css";

const SYNTHETIC_WORKSPACE_ID = "00000000-0000-4000-8000-000000000001";
const SYNTHETIC_SUBJECT_ID = "00000000-0000-4000-8000-000000000002";
const DEFAULT_INQUIRY =
  "合成大学询单：CAS 50-00-0，25 g，用于分析研究，交付至美国境内的合成测试地址。";

export interface ReviewedAIRun {
  readonly useCaseKey: string;
  readonly invocationId: string;
  readonly decision: Exclude<AIReviewDecision, "RETRY">;
}

export interface AIWorkbenchProps {
  readonly workspaceId?: string;
  readonly subjectId?: string;
  readonly inquiryText?: string;
  readonly embedded?: boolean;
  readonly onReviewed?: (run: ReviewedAIRun) => void;
}

export function AIWorkbench({
  workspaceId = SYNTHETIC_WORKSPACE_ID,
  subjectId = SYNTHETIC_SUBJECT_ID,
  inquiryText = DEFAULT_INQUIRY,
  embedded = false,
  onReviewed,
}: AIWorkbenchProps = {}) {
  const [provider, setProvider] = useState<AIProviderStatus>();
  const [useCases, setUseCases] = useState<readonly AIUseCase[]>([]);
  const [selected, setSelected] = useState("inquiry_quality_judgment_v1");
  const [resultUseCase, setResultUseCase] = useState(
    "inquiry_quality_judgment_v1",
  );
  const [inquiry, setInquiry] = useState(inquiryText);
  const [result, setResult] = useState<AIRunView>();
  const [reviewStatus, setReviewStatus] = useState("PENDING");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<
    "judgment" | "prompts" | "evaluation" | "usage"
  >("judgment");

  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([
      loadProviderStatus(controller.signal),
      loadUseCases(controller.signal),
    ])
      .then(([status, registry]) => {
        setProvider(status);
        setUseCases(registry);
      })
      .catch((failure: unknown) => {
        if (!controller.signal.aborted)
          setError(
            failure instanceof Error ? failure.message : "AI 平台暂时不可用",
          );
      });
    return () => controller.abort();
  }, []);

  useEffect(() => setInquiry(inquiryText), [inquiryText]);

  const selectedUseCase = useMemo(
    () => useCases.find((item) => item.code === selected),
    [selected, useCases],
  );
  const run = async () => {
    setBusy(true);
    setError("");
    try {
      const created = await createAIRun(
        currentRunInput(selected, inquiry, workspaceId, subjectId),
      );
      setResultUseCase(selected);
      setResult(created);
      setReviewStatus("PENDING");
      if (!["COMPLETED", "FAILED", "CANCELLED"].includes(created.status)) {
        const completed = await pollRun(created.invocationId);
        setResult(completed);
      }
    } catch (failure: unknown) {
      setError(failure instanceof Error ? failure.message : "AI 运行失败");
    } finally {
      setBusy(false);
    }
  };

  const review = async (decision: Exclude<AIReviewDecision, "RETRY">) => {
    if (!result) return;
    setBusy(true);
    setError("");
    try {
      await reviewAIRun(
        result.invocationId,
        decision,
        decision === "ACCEPT_WITH_CHANGES"
          ? { humanChangesRequired: true }
          : {},
      );
      const reviewed = await loadAIRun(result.invocationId);
      setResult(reviewed);
      setReviewStatus(decision);
      onReviewed?.({
        useCaseKey: resultUseCase,
        invocationId: result.invocationId,
        decision,
      });
    } catch (failure: unknown) {
      setError(failure instanceof Error ? failure.message : "AI 复核失败");
    } finally {
      setBusy(false);
    }
  };

  const retry = async () => {
    if (!result) return;
    setBusy(true);
    setError("");
    try {
      const created = await retryAIRun(
        result.invocationId,
        currentRunInput(selected, inquiry, workspaceId, subjectId),
      );
      setResult(created);
      setReviewStatus("PENDING");
      const completed = await pollRun(created.invocationId);
      setResult(completed);
    } catch (failure: unknown) {
      setError(failure instanceof Error ? failure.message : "AI 重试失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className={embedded ? "ai-workbench is-embedded" : "ai-workbench"}
      aria-label="AI 平台开发工作台"
    >
      <header className="ai-workbench__header">
        <div>
          <span className="ai-kicker">有限人工 AI · CRM-AI-25G</span>
          <h1>AI 平台工作台</h1>
          <p>针对合成数据进行辅助复核，不改变正式 CRM 业务流程。</p>
        </div>
        <div className="ai-lock-badge">DEEPSEEK · deepseek-v4-flash</div>
      </header>
      <nav className="ai-tabs" aria-label="AI 工作台分区">
        {(["judgment", "prompts", "evaluation", "usage"] as const).map(
          (item) => (
            <button
              key={item}
              type="button"
              className={tab === item ? "is-active" : ""}
              onClick={() => setTab(item)}
            >
              {item === "judgment"
                ? "判断"
                : item === "prompts"
                  ? "提示词"
                  : item === "evaluation"
                    ? "评估"
                    : "用量"}
            </button>
          ),
        )}
      </nav>
      {error ? (
        <div className="ai-alert" role="alert">
          {error}
        </div>
      ) : null}
      <section className="ai-provider-card">
        <div>
          <span className="ai-label">AI 服务商状态</span>
          <strong>{provider?.provider ?? "加载中…"}</strong>
          <small>
            {provider?.expectedModelVersion ?? "模型版本门禁待确认"}
          </small>
        </div>
        <div
          className={
            provider?.statuses.some((item) => item.health.available)
              ? "ai-health is-up"
              : "ai-health"
          }
        >
          {provider?.statuses.some((item) => item.health.available)
            ? "就绪"
            : "已阻止 / 仅人工"}
        </div>
        <div className="ai-policy-list">
          <span>AI 服务商回退：已阻止</span>
          <span>模型回退：已阻止</span>
          <span>自动应用：关闭</span>
        </div>
      </section>
      {tab === "judgment" ? (
        <section className="ai-grid">
          <div className="ai-card">
            <div className="ai-card__heading">
              <div>
                <span className="ai-label">合成数据运行</span>
                <h2>询单判断</h2>
              </div>
              <span className="ai-mode">
                {aiModeLabel(
                  selectedUseCase?.executionMode ?? "THINK_THEN_STRICT",
                )}{" "}
                · 思考模式 {aiModeLabel(selectedUseCase?.thinking ?? "enabled")}{" "}
                · 推理强度{" "}
                {aiModeLabel(selectedUseCase?.reasoningEffort ?? "high")}
              </span>
            </div>
            <label>
              AI 用例
              <select
                value={selected}
                onChange={(event) => {
                  setSelected(event.target.value);
                  setResult(undefined);
                  setReviewStatus("PENDING");
                }}
              >
                {useCases.length ? (
                  useCases.map((item) => (
                    <option key={item.code} value={item.code}>
                      {useCaseLabel(item.code)}
                    </option>
                  ))
                ) : (
                  <option value="inquiry_quality_judgment_v1">
                    询单质量判断
                  </option>
                )}
              </select>
            </label>
            <label>
              不受信任的合成询单
              <textarea
                value={inquiry}
                onChange={(event) => setInquiry(event.target.value)}
                rows={7}
              />
            </label>
            <button
              className="ai-primary"
              type="button"
              onClick={() => void run()}
              disabled={busy}
            >
              {busy ? "运行中…" : "运行辅助判断"}
            </button>
            <p className="ai-note">
              结果是经过校验的辅助数据；接受、修改、拒绝和重试仍由人工决定。
            </p>
          </div>
          <div className="ai-card ai-result-card">
            <div className="ai-card__heading">
              <div>
                <span className="ai-label">结果</span>
                <h2>证据与复核</h2>
              </div>
              <span className="ai-review">需要人工复核</span>
            </div>
            {result ? (
              <RunResult result={result} reviewStatus={reviewStatus} />
            ) : (
              <div className="ai-empty">
                尚未运行。请使用合成数据检查业务合同。
              </div>
            )}
            <div className="ai-actions">
              <button
                type="button"
                disabled={!result || busy}
                onClick={() => void review("ACCEPT")}
              >
                接受
              </button>
              <button
                type="button"
                disabled={!result || busy}
                onClick={() => void review("ACCEPT_WITH_CHANGES")}
              >
                修改后接受
              </button>
              <button
                type="button"
                disabled={!result || busy}
                onClick={() => void review("REJECT")}
              >
                拒绝
              </button>
              <button
                type="button"
                disabled={!result || busy}
                onClick={() => void retry()}
              >
                重试
              </button>
            </div>
            <p className="ai-note">
              复核是仅追加的辅助决定，绝不会直接修改 CRM 业务记录。
            </p>
          </div>
        </section>
      ) : (
        <section className="ai-card ai-placeholder">
          <h2>
            {tab === "prompts"
              ? "提示词"
              : tab === "evaluation"
                ? "评估"
                : "用量"}
          </h2>
          <p>
            开发模块已建立基础结构，核心端点、不可变提示词版本、合成评估和用量合同均可接入。
          </p>
          {tab === "prompts" ? (
            <p>发布受权限控制，且不会重写既有提示词版本。</p>
          ) : null}
          {tab === "evaluation" ? (
            <p>金丝雀验证和 200 个样本的回归门禁用于阻止模型漂移。</p>
          ) : null}
          {tab === "usage" ? (
            <p>
              可查看工作空间、用例、模型、Token、缓存命中
              Token、成本、预算和失败情况，不显示任何密钥。
            </p>
          ) : null}
        </section>
      )}
    </main>
  );
}

async function pollRun(invocationId: string): Promise<AIRunView> {
  let latest: AIRunView | undefined;
  for (let attempt = 0; attempt < 10; attempt += 1) {
    latest = await loadAIRun(invocationId);
    if (
      ["SUCCEEDED", "REVIEW_REQUIRED", "FAILED", "CANCELLED"].includes(
        latest.integrationStatus,
      )
    )
      return latest;
    await new Promise((resolve) => window.setTimeout(resolve, 750));
  }
  if (latest) return latest;
  throw new Error("AI 运行状态暂时不可用");
}

function RunResult({
  result,
  reviewStatus,
}: {
  result: AIRunView;
  reviewStatus: string;
}) {
  const usage = result.usageRecords ?? [];
  const totalInput = usage.reduce(
    (sum, item) => sum + (item.inputUnits ?? 0),
    0,
  );
  const totalOutput = usage.reduce(
    (sum, item) => sum + (item.outputUnits ?? 0),
    0,
  );
  const totalCache = usage.reduce(
    (sum, item) => sum + (item.cacheHitUnits ?? 0),
    0,
  );
  const totalCost = usage.reduce(
    (sum, item) => sum + Number(item.estimatedCost ?? 0),
    0,
  );
  const totalLatency = usage.reduce(
    (sum, item) => sum + (item.latencyMs ?? 0),
    0,
  );
  const confidence = result.structuredOutput?.confidence;
  const evidence = Array.isArray(result.structuredOutput?.evidence)
    ? result.structuredOutput.evidence
    : [];
  const missingInformationSource =
    result.structuredOutput?.missingInformation ??
    result.structuredOutput?.missingFields;
  const missingInformation = Array.isArray(missingInformationSource)
    ? missingInformationSource
    : [];
  const riskSignals = Array.isArray(result.structuredOutput?.riskSignals)
    ? result.structuredOutput.riskSignals
    : [];
  const commercialPotential = displayFact(
    result.structuredOutput?.commercialPotential,
  );
  const purchaseIntent = displayFact(result.structuredOutput?.purchaseIntent);
  const businessFit = displayFact(result.structuredOutput?.businessFit);
  const recommendedTeam = displayFact(
    result.structuredOutput?.recommendedTeam ??
      result.structuredOutput?.routingRecommendation,
  );
  const recommendedPriority = displayFact(
    result.structuredOutput?.recommendedPriority,
  );
  return (
    <div className="ai-result">
      <dl className="ai-metrics">
        <Metric label="执行模式" value={aiModeLabel(result.executionMode)} />
        <Metric label="模型" value={result.model} />
        <Metric label="逻辑运行 ID" value={result.logicalRunId} />
        <Metric
          label="服务商请求次数"
          value={String(result.providerRequestCount)}
        />
        <Metric
          label="最终结构校验"
          value={statusLabel(result.finalSchemaStatus)}
        />
        <Metric label="状态" value={statusLabel(result.integrationStatus)} />
        <Metric label="复核" value={statusLabel(reviewStatus)} />
        <Metric
          label="置信度"
          value={typeof confidence === "number" ? confidence.toFixed(2) : "—"}
        />
        <Metric label="商业潜力" value={commercialPotential} />
        <Metric label="采购意向" value={purchaseIntent} />
        <Metric label="业务匹配度" value={businessFit} />
        <Metric
          label="Token"
          value={`${totalInput} 输入 · ${totalOutput} 输出 · ${totalCache} 缓存命中`}
        />
        <Metric label="成本" value={`$${totalCost.toFixed(6)}`} />
        <Metric label="延迟" value={`${totalLatency} 毫秒`} />
      </dl>
      <div className="ai-pass-list" aria-label="AI 服务商调用账本">
        {usage.map((item) => (
          <span key={`${item.passType}-${item.providerAttempt}`}>
            {aiModeLabel(item.passType)} #{item.providerAttempt} ·{" "}
            {statusLabel(item.validationStatus)}
          </span>
        ))}
      </div>
      {evidence.length ? (
        <p className="ai-evidence">证据记录：{evidence.length}</p>
      ) : null}
      <div className="ai-review-facts" aria-label="辅助复核信息">
        <p>缺失信息：{missingInformation.length}</p>
        <p>风险信号：{riskSignals.length}</p>
        <p>建议团队：{recommendedTeam}</p>
        <p>建议优先级：{recommendedPriority}</p>
      </div>
      <span className="ai-label">最终结构化结果（原始协议字段）</span>
      <pre>
        {JSON.stringify(
          result.structuredOutput ?? { status: result.status },
          null,
          2,
        )}
      </pre>
    </div>
  );
}

function displayFact(value: unknown): string {
  if (typeof value !== "string" || value.length === 0) return "—";
  return /^[A-Z][A-Z0-9_]*$/.test(value) ? statusLabel(value) : value;
}

function currentRunInput(
  useCaseKey: string,
  inquiryText: string,
  workspaceId: string,
  subjectId: string,
) {
  return {
    useCaseKey,
    workspaceId,
    subjectId,
    input: {
      inquiryText,
      sourceType: "SYNTHETIC",
      companyDomain: "synthetic.invalid",
    },
  };
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd title={value}>{value}</dd>
    </div>
  );
}
